import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { j515Api, ApproveCandidate, ApproveItem } from '../../services/j515Api';
import { BatchAcceptanceBridge, pwj515f1AcceptanceManifest } from '../../features/acceptance';
import './J515Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ApproveCandidate;
  selected: boolean;
}

// ---- Component ----
const J515Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await j515Api.queryCandidates(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的研發託工單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fj610_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute approval ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要核准的研發託工單');
      return;
    }
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆研發託工單？此操作不可復原。`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const approveItems: ApproveItem[] = items.map((s) => ({
          no: s.item.fj610_01,
          checked: s.selected,
        }));
        setExecuting(true);
        try {
          const res = await j515Api.approve({ items: approveItems });
          if (res.failed === 0) {
            message.success(res.message || `核准完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '核准執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fj610_01, e.target.checked)}
        />
      ),
    },
    {
      title: '託工單號',
      dataIndex: ['item', 'fj610_01'],
      key: 'fj610_01',
      width: 100,
    },
    {
      title: '託工日期',
      dataIndex: ['item', 'fj610_11'],
      key: 'fj610_11',
      width: 110,
    },
    {
      title: '廠商代碼',
      dataIndex: ['item', 'fj610_12'],
      key: 'fj610_12',
      width: 100,
    },
    {
      title: '廠商名稱',
      dataIndex: ['item', 'fj610_13'],
      key: 'fj610_13',
      width: 160,
    },
    {
      title: '金額',
      dataIndex: ['item', 'fj610_14'],
      key: 'fj610_14',
      width: 110,
      align: 'right',
      render: (val: number) =>
        val != null ? val.toLocaleString('zh-TW', { minimumFractionDigits: 2 }) : '0.00',
    },
    {
      title: '部門',
      dataIndex: ['item', 'dept_name'],
      key: 'dept_name',
      width: 100,
    },
    {
      title: '狀態',
      dataIndex: ['item', 'fj610_80'],
      key: 'fj610_80',
      width: 70,
      align: 'center',
      render: (val: string) => (val === 'P' ? '已核准' : '待核准'),
    },
  ];

  // ---- Render ----
  return (
    <div className="j515-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                研發管理
              </a>
              <span>/</span>
              <span className="current">研發託工單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>研發託工單核准</h1>
            <span className="badge">J515</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="fj610_01">託工單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fj610_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入託工單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwj515f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fj610_01)}
          keyOf={(item) => item.fj610_01}
          queryExact={async (key) => (await j515Api.queryCandidates(key, key)).items?.[0] || null}
          lastApiResponse={items}
          loading={loading || executing}
          onAutoFill={() => setItems(prev => prev.map((state, index) => ({ ...state, selected: index === 0 })))}
          autoFillDisabled={items.length === 0}
          autoFillReason="只選取正式 API 回傳的第一筆研發託工核准候選。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default J515Form;
